import { useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { useNavigate, useParams, useSearchParams } from 'react-router'

import { Icon } from '@/components/icon'
import { Render } from '@/components/render-if'
import { Badge, Button } from '@/components/ui'
import { useCancelarReservaProduto } from '@/features/empreendimentos/hooks'
import type { TGetDetalhesLoteResponse } from '@/features/empreendimentos/http'
import { useAuth, useProdutoDisponivel } from '@/hooks'
import { showToast } from '@/infra/sonner'
import { DestructiveActionGuard } from '@/shared/destructive-action-guard'
import { TipoProdutoEnum, type TProdutoStatusEnum } from '@/types/produto'
import { storageKeys } from '@/utils/constants'
import { formatToBRL } from '@/utils/currency.utils'
import { formatDateToPtBr } from '@/utils/date.utils'
import { produtosKeys } from '@/utils/produtos-query-keys'
import { configuracaoPorStatusProduto, descricaoPorTipoProduto } from '@/utils/status-config'

import { DetalhesLoteSkeleton } from './detalhes-lote.skeleton'
import { InfoItem } from './info-item'

interface DetalhesLoteProps {
  detalhesLote?: TGetDetalhesLoteResponse
  isLoading: boolean
  idExternoEmpreendimento: string
  nomeEmpreendimento: string
}

export const DetalhesLote = ({
  detalhesLote,
  isLoading,
  idExternoEmpreendimento,
  nomeEmpreendimento,
}: DetalhesLoteProps) => {
  const { t } = useTranslation('espelho-de-vendas')
  const [_, setSearchParams] = useSearchParams()
  const params = useParams()
  const navigate = useNavigate()

  const { refetch: getProdutoDisponivel } = useProdutoDisponivel(detalhesLote?.idExterno ?? 0)
  const { mutate: cancelarReserva } = useCancelarReservaProduto()

  const { user } = useAuth()
  const handleClose = () => {
    setSearchParams((state) => {
      state.delete('lote')
      return state
    })
  }
  const loadingLote = isLoading || !detalhesLote
  const isLoteDisponivel = detalhesLote?.status === 'Disponivel'
  const usuarioLogadoResponsavelReserva =
    detalhesLote?.status === 'Reservada' && detalhesLote.reserva?.idResponsavel === user?.usuarioLogadoId

  const onCancelarReserva = (idExternoProduto: number) => cancelarReserva({ idExternoProduto })

  const badgeVariant = (lote: TGetDetalhesLoteResponse) => configuracaoPorStatusProduto[lote.tipoLegendaStatus].variant
  const badgeText = descricaoPorTipoProduto[TipoProdutoEnum.Lote][detalhesLote?.status as TProdutoStatusEnum]

  const queryClient = useQueryClient()
  const handleSimularLote = async () => {
    const response = await getProdutoDisponivel()
    sessionStorage.removeItem(storageKeys.simulador_form)
    if (!response.data) {
      showToast({ message: t('erro_produto.indisponivel'), variant: 'warning' })
      queryClient.invalidateQueries({ queryKey: produtosKeys.detalheLote(detalhesLote!.idExterno) })
      queryClient.invalidateQueries({
        queryKey: produtosKeys.lotesEmpreendimento(String(idExternoEmpreendimento)),
      })
      return
    }

    navigate('/simulador', {
      state: {
        empreendimento: { nome: nomeEmpreendimento, idExterno: params.idExternoEmpreendimento },
        idExternoBloco: detalhesLote?.idExternoBloco,
        idExternoProduto: detalhesLote?.idExterno,
        tipoDoProduto: 'Lote',
      },
    })
  }

  return (
    <div className="shadow-cz animate-in slide-in-from-left-5 fade-in absolute top-0 right-0 z-50 mt-1 mr-1 h-full max-h-fit w-full max-w-102 overflow-auto rounded-2xl bg-white px-6 pt-1 pb-6 duration-300 md:w-102">
      <Button variant="ghost" onClick={handleClose} className="absolute top-4 right-4 p-1">
        <Icon name="close" size={24} className="text-primary" />
      </Button>
      {loadingLote ? (
        <DetalhesLoteSkeleton />
      ) : (
        <section className="mt-10 flex flex-col gap-1">
          <header className="flex flex-col gap-2">
            <h2 className="text-2xl">{t('detalhes_lote.titulo', { nome: detalhesLote.nome })}</h2>
            <Badge variant={badgeVariant(detalhesLote)}>{badgeText}</Badge>
          </header>
          <div className="overflow-auto">
            <div className="mt-6 mb-6 text-sm">
              <InfoItem label={t('detalhes_lote.labels.quadra')} value={detalhesLote.quadra} />
              <InfoItem label={t('detalhes_lote.labels.metragem')} value={detalhesLote.metragem} />
              <InfoItem label={t('detalhes_lote.labels.valor')} value={formatToBRL(detalhesLote.valor || 0)} bold />
              <Render.If condition={!isLoteDisponivel}>
                {() => {
                  return (
                    <InfoItem
                      label={t('detalhes_lote.labels.situacao')}
                      value={detalhesLote.detalhamentoStatus ?? '-'}
                    />
                  )
                }}
              </Render.If>
            </div>
            <Render.If condition={detalhesLote.reserva?.nomeResponsavel}>
              {() => (
                <div className="mb-10">
                  <h4 className="mb-2 text-base">{t('detalhes_lote.reserva.titulo')}</h4>
                  <Render.If condition={detalhesLote.reserva?.dataExpiracao}>
                    {(dataExpiracao) => {
                      return (
                        <InfoItem
                          data-testid="reserva-expiracao"
                          label={t('detalhes_lote.reserva.expira_as')}
                          value={formatDateToPtBr(dataExpiracao, {
                            day: undefined,
                            month: undefined,
                            year: undefined,
                            hour: '2-digit',
                            minute: '2-digit',
                          })}
                        />
                      )
                    }}
                  </Render.If>
                  <InfoItem
                    data-testid="reserva-responsavel"
                    label={t('detalhes_lote.reserva.responsavel')}
                    value={detalhesLote?.reserva?.nomeResponsavel}
                  />
                </div>
              )}
            </Render.If>
          </div>
          <div className="flex gap-6">
            <Render.If condition={isLoteDisponivel}>
              {() => {
                return (
                  <Button variant="default" className="w-full" onClick={handleSimularLote}>
                    {t('detalhes_lote.simular_lote')}
                  </Button>
                )
              }}
            </Render.If>
            <Render.If condition={usuarioLogadoResponsavelReserva}>
              {() => {
                return (
                  <div className="flex w-full gap-6">
                    <DestructiveActionGuard
                      showCloseButton
                      title={t('detalhes_lote.reserva.modal_cancelar_reserva.titulo')}
                      description={t('detalhes_lote.reserva.modal_cancelar_reserva.descricao')}
                    >
                      <Button
                        variant="outline"
                        className="w-full"
                        onClick={() => onCancelarReserva(detalhesLote.idExterno)}
                      >
                        {t('detalhes_lote.reserva.cancelar_reserva')}
                      </Button>
                    </DestructiveActionGuard>
                    <Button variant="default" className="w-full" onClick={handleSimularLote}>
                      {t('detalhes_lote.simular_lote')}
                    </Button>
                  </div>
                )
              }}
            </Render.If>
          </div>
        </section>
      )}
    </div>
  )
}
